import React, {useState, useEffect, useMemo} from "react";
import {useTable, usePagination, useSortBy, useExpanded} from "react-table";
import { QueryClient, QueryClientProvider, useQuery } from 'react-query'
import {JOURNAL_DETAILED_COLUMNS} from "./columns";
import { Col, Row} from 'reactstrap';
import {withRouter} from "react-router-dom";
import {connect} from "react-redux";
import {clone} from 'lodash';
import JournalDetailedFilter from "./JournalDetailedFilter";
import {
  reducer,
  useEffectToDispatch,
  fetchApiData,
  fetchApiDataExport,
  initialState,
  Sorting
} from "@/shared/apiTableHelper"
import {getFirstDay, formatYMD, getReportName} from '@/shared/helpers'
import { StickyContainer, Sticky } from "react-sticky";

const queryClient = new QueryClient()

// Accessors whose export column key differs from the CSV header key returned by the backend
const ACCESSOR_TO_EXPORT_KEY = { shownId: 'account_holder_id' };

// Backend-only export fields that aren't rendered as a screen column but still count toward "is this row empty"
const EXPORT_ONLY_NUMERIC_KEYS = ['discount_rebate_credited_to_program', 'expiration_rebate_credited_to_program'];

const NUMERIC_FIELDS = JOURNAL_DETAILED_COLUMNS
  .flatMap((group) => group.columns)
  .map((column) => column.accessor)
  .filter((accessor) => accessor && accessor !== 'name' && accessor !== 'shownId');

const EXPORT_NUMERIC_KEYS = [
  ...NUMERIC_FIELDS.map((field) => ACCESSOR_TO_EXPORT_KEY[field] || field),
  ...EXPORT_ONLY_NUMERIC_KEYS
];

const sumField = (row, field) => {
  let total = Number(row.original?.[field]) || 0;
  if (row.subRows && row.subRows.length > 0) {
    total += row.subRows.reduce((sum, subRow) => sum + sumField(subRow, field), 0);
  }
  return total;
};

const isRowEmpty = (row) => NUMERIC_FIELDS.every((field) => !sumField(row, field));

const isExportRowEmpty = (row) => EXPORT_NUMERIC_KEYS.every((key) => {
  const value = row[key];
  return value === null || value === undefined || value === '' || Number(value) === 0;
});

const DataTable = ({organization, programs}) => {

  const defaultFrom = formatYMD(getFirstDay());
  const [filter, setFilter] = useState({programs: programs, awardLevels: [], from: defaultFrom, to: formatYMD(new Date())});
    const [useFilter, setUseFilter] = useState(false);
    const [trigger, setTrigger] = useState(0);
    const [exportData, setExportData] = useState([]);
    const [exportHeaders, setExportHeaders] = useState([]);
    const [exportToCsv, setExportToCsv] = useState(false);
    const exportLink = React.createRef();
    const [isExporting, setIsExporting] = useState(false);
    const [hideEmptyRows, setHideEmptyRows] = useState(false);

    useEffect(() => {
      if (exportToCsv) {
        if (exportLink.current) {
          setExportToCsv(false);
          exportLink.current.link.click();
        }
      }
    }, [exportLink])

    useEffect(()=>{
      setFilter({programs: programs, awardLevels: [], from: defaultFrom, to: new Date()})
    }, [programs])

    const getVisibleExportKeys = () => allColumns
      .filter((column) => !column.columns && column.id !== 'expander' && !hiddenColumns.includes(column.id))
      .map((column) => ACCESSOR_TO_EXPORT_KEY[column.id] || column.id);

    const download = async (filterValues) => {
      try {
        setIsExporting(true);
      let tmpFilter = clone(filterValues);
      tmpFilter.limit = pageSize;
      tmpFilter.page = pageIndex+1;
      tmpFilter.exportToCsv = 1;
      const response = await fetchApiDataExport(
        {
          url: apiUrl,
          filter: tmpFilter,
          sortby: queryPageSortBy,
          trigger: queryTrigger,
          visibleColumns: getVisibleExportKeys()
        }
      );
      const results = hideEmptyRows
        ? (response.results || []).filter((row) => !isExportRowEmpty(row))
        : response.results;
      setExportData(results);
      setExportHeaders(response.headers);
      setExportToCsv(true);
      } catch (err) {
        console.error("Export failed:", err);
      } finally {
        setIsExporting(false);
      }
    }
   
    let program_columns = [
        ...JOURNAL_DETAILED_COLUMNS,
    ]
    let columns = useMemo( () => program_columns, [])

    const defaultColumn = React.useMemo(
        () => ({
          // minWidth: 150,
        }),
        []
    )

    const [{ queryPageIndex, queryPageSize, totalCount, queryPageFilter, queryPageSortBy, queryTrigger}, dispatch] =
      React.useReducer(reducer, initialState);

    const apiUrl = `/organization/${organization.id}/report/journal-detailed`;

    const { isLoading, error, data, isSuccess } = useQuery(
      ['', apiUrl, queryPageIndex, queryPageSize, queryPageFilter, queryPageSortBy, queryTrigger],
      () => fetchApiData(
        {
          url: apiUrl,
          page: queryPageIndex,
          size: queryPageSize,
          filter,
          sortby: queryPageSortBy,
          trigger: queryTrigger
        }
      ),
      {
        keepPreviousData: true,
        staleTime: Infinity,
      }
    );

    const totalPageCount = Math.ceil(totalCount / queryPageSize)

    // console.log(data)

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    page,
    pageCount,
    pageOptions,
    gotoPage,
    previousPage,
    canPreviousPage,
    nextPage,
    canNextPage,
    setPageSize,
    allColumns,
    toggleAllRowsExpanded,
    isAllRowsExpanded,
    state: { pageIndex, pageSize, sortBy, hiddenColumns }
  } = useTable({
      columns,
      data: data ? data.results : [],
      initialState: {
        pageIndex: queryPageIndex,
        pageSize: queryPageSize,
        sortBy: queryPageSortBy,
      },
      manualPagination: true, // Tell the usePagination
      pageCount: data ? totalPageCount : null,
      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetPage: false,
      defaultColumn,

    },
    useSortBy,
    useExpanded,
    usePagination,
  );

    useEffectToDispatch(dispatch, {pageIndex, pageSize, gotoPage, sortBy, filter, data, useFilter, trigger});

    if (error) {
        return <p>Error: {JSON.stringify(error)}</p>;
    }

    if (isLoading || !organization?.id) {
      return <p>Loading...</p>;
    }

    if (isSuccess)
      return (
        <StickyContainer>
          <div className='table react-table report-table'>
            <div className="action-panel">
              <Row className="mx-0">
                <Col lg={12} md={12} sm={12}>
                  <JournalDetailedFilter  filter={filter} setFilter={setFilter} useFilter={useFilter} setUseFilter={setUseFilter}
                                         exportData={exportData} exportLink={exportLink} exportHeaders={exportHeaders}
                                         download={download} isExporting={isExporting} reportName={getReportName('Journal Detail')}
                                         allColumns={allColumns} hideEmptyRows={hideEmptyRows} setHideEmptyRows={setHideEmptyRows}
                                         toggleAllRowsExpanded={toggleAllRowsExpanded} isAllRowsExpanded={isAllRowsExpanded}/>
                </Col>
              </Row>
            </div>
            {
              isLoading && <p>Loading...</p>
            }
            {
              isSuccess &&
              <table {...getTableProps()} style={{background: '#fff', tableLayout: 'auto'}} className="table table--bordered">
                <thead style={{ top: '60px', zIndex: '2', position: 'sticky', background: '#f9fafb' }}>
                {headerGroups.map((headerGroup, i) => {
                  const isColumnRow = i === headerGroups.length - 1;
                  return (
                    <tr {...headerGroup.getHeaderGroupProps()} style={{ borderBottom: isColumnRow ? '2px solid #e5e7eb' : '1px solid #e5e7eb' }}>
                      {headerGroup.headers.map((column, j) => (
                        <th {...column.getHeaderProps(column.getSortByToggleProps())} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', ...(j > 0 ? { borderLeft: '1px solid #e5e7eb' } : {}) }}>
                          {column.render('Header')}
                          {column.isSorted ? <Sorting column={column}/> : ''}
                        </th>
                      ))}
                    </tr>
                  );
                })}
                </thead>
                <tbody className="table table--bordered" {...getTableBodyProps()}>
                {page.map(row => {
                  prepareRow(row);
                  if (row.original.label && Object.keys(row.original).length === 1) {
                    return null; // skip rendering this row
                  }
                  if (hideEmptyRows && isRowEmpty(row)) {
                    return null;
                  }
                  return (
                    <tr {...row.getRowProps({className: row.isExpanded && row.original.dinamicDepth === 1 ? ' totalRow' : ''})} >
                      {
                        row.cells.map(cell => {
                          return <td {...cell.getCellProps({
                            className: row.isExpanded && row.original.dinamicDepth === 1 ? cell.column.className + ' totalRow' : cell.column.className,
                            style: {
                              textAlign: 'left',
                              ...(row.cells.indexOf(cell) > 0 ? { borderLeft: '1px solid #e5e7eb' } : {}),
                              padding: '8px 12px',
                            }})} >
                            {cell.render('Cell')}
                          </td>
                        })
                      }
                    </tr>
                  )
                })}
                </tbody>
                <tfoot>
                {footerGroups.map((footerGroup) => (
                  <tr {...footerGroup.getFooterGroupProps()}>
                    {footerGroup.headers.map(column => (
                      <th {...column.getFooterProps()} style={{ padding: '10px 12px', textAlign: 'left', borderTop: '2px solid #e5e7eb', fontWeight: 600 }}>{column.render('Footer')}</th>
                    ))}
                  </tr>
                ))}
                </tfoot>
              </table>
            }

          </div>
        </StickyContainer>
      )
}

const TableWrapper = ({organization, programs}) => {
  if (!organization || !programs) return 'Loading...'
  return (
    <QueryClientProvider client={queryClient}>
      <DataTable organization={organization} programs={programs}/>
    </QueryClientProvider>
  )
}

export default withRouter(connect((state) => ({
  organization: state.organization
}))(TableWrapper));
